// ============================================================================
// AdminEventsPanel — top-level Events catalog.
// ============================================================================
// Lists every event the UIF admin can see (past + upcoming, drafts +
// published, optionally templates). Per-row actions: Edit, View detail,
// Copy slug URL. Click a column header to sort.
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import {
  Plus, Upload, Pencil, ExternalLink, Search, RefreshCw, Copy,
  Repeat as RepeatIcon, ArrowUp, ArrowDown, Send, Eye, X, Loader2, DollarSign,
} from 'lucide-react';
import { api, type PublicEvent } from '../api';

interface Props {
  token: string;
}

type SortKey = 'title' | 'date' | 'series' | 'audience' | 'status';
type SortDir = 'asc' | 'desc';

export function AdminEventsPanel({ token }: Props) {
  const [events, setEvents] = useState<PublicEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [scope, setScope] = useState<'upcoming' | 'all' | 'templates' | 'drafts'>('upcoming');
  // Sort state. Default: Date ascending (soonest first) so the default
  // 'Upcoming' scope reads as a natural countdown — next event at top. Click
  // the Date column header to flip to descending when reviewing All events
  // historically.
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  // Bulk-select state. Selection is keyed by calendar_entry_id (the ID the
  // backend uses for PATCH /api/admin/events/:id). Cleared whenever the
  // scope filter changes so you can't accidentally publish events you
  // can't currently see.
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    setLoading(true); setError('');
    try {
      // includeTemplates + includeDrafts so the admin sees everything they
      // own — not just live events.
      const d = await api.events({ includePast: true, includeTemplates: true, includeDrafts: true, token });
      setEvents(d.events);
    } catch (err: any) {
      setError(err?.message || 'Could not load events.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // ----- Filter (scope + search) -----
  const filtered = useMemo(() => {
    const now = Date.now();
    let scoped = events;
    if (scope === 'upcoming') {
      scoped = events.filter((e) => !e.is_recurring_template && new Date(e.start_date).getTime() >= now - 24 * 60 * 60 * 1000);
    } else if (scope === 'templates') {
      scoped = events.filter((e) => e.is_recurring_template);
    } else if (scope === 'drafts') {
      scoped = events.filter((e) => e.is_draft && !e.is_recurring_template);
    } else {
      // 'all' — exclude templates by default in the list (they get their own scope).
      scoped = events.filter((e) => !e.is_recurring_template);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      scoped = scoped.filter((e) =>
        e.title.toLowerCase().includes(q) ||
        (e.slug || '').toLowerCase().includes(q) ||
        (e.series_label || '').toLowerCase().includes(q)
      );
    }
    return scoped;
  }, [events, search, scope]);

  // ----- Sort -----
  const sorted = useMemo(() => {
    const list = [...filtered];
    const cmp = (a: PublicEvent, b: PublicEvent): number => {
      let av: string | number = '';
      let bv: string | number = '';
      switch (sortKey) {
        case 'title':
          av = a.title.toLowerCase(); bv = b.title.toLowerCase(); break;
        case 'date':
          av = new Date(a.start_date).getTime(); bv = new Date(b.start_date).getTime(); break;
        case 'series':
          // Empty series last regardless of direction
          av = (a.series_label || '￿').toLowerCase(); bv = (b.series_label || '￿').toLowerCase(); break;
        case 'audience':
          av = (a.audience || 'public').toLowerCase(); bv = (b.audience || 'public').toLowerCase(); break;
        case 'status':
          av = a.is_draft ? 1 : 0; bv = b.is_draft ? 1 : 0; break;
      }
      if (av < bv) return -1;
      if (av > bv) return 1;
      return 0;
    };
    list.sort(cmp);
    if (sortDir === 'desc') list.reverse();
    return list;
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      // Default direction: date defaults to desc (newest first), everything else asc.
      setSortDir(key === 'date' ? 'desc' : 'asc');
    }
  };

  // Clear selection when the scope filter changes so the action bar always
  // reflects what's actually on screen.
  useEffect(() => { setSelected(new Set()); }, [scope]);

  const toggleSelect = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const visibleIds = sorted.map((e) => e.calendar_entry_id);
    const allSelected = visibleIds.every((id) => selected.has(id));
    setSelected(allSelected ? new Set() : new Set(visibleIds));
  };

  // Selected counts split by current is_draft state — drives which bulk
  // action makes sense to offer.
  const selectionSummary = useMemo(() => {
    let drafts = 0;
    let live = 0;
    for (const e of sorted) {
      if (!selected.has(e.calendar_entry_id)) continue;
      if (e.is_draft) drafts++; else live++;
    }
    return { drafts, live, total: drafts + live };
  }, [sorted, selected]);

  const runBulk = async (action: 'go-live' | 'unpublish') => {
    const targetIds = sorted
      .filter((e) => selected.has(e.calendar_entry_id))
      .filter((e) => action === 'go-live' ? e.is_draft : !e.is_draft)
      .map((e) => e.calendar_entry_id);
    if (targetIds.length === 0) return;
    const verb = action === 'go-live' ? 'publish' : 'move back to draft';
    const yes = window.confirm(
      `${verb.charAt(0).toUpperCase() + verb.slice(1)} ${targetIds.length} event${targetIds.length === 1 ? '' : 's'}? ` +
      (action === 'go-live'
        ? 'They will be visible on the public calendar immediately.'
        : 'They will be removed from the public calendar.'),
    );
    if (!yes) return;
    setBulkBusy(true);
    const failed: number[] = [];
    await Promise.all(
      targetIds.map((id) =>
        api.updateEvent(token, id, { is_draft: action !== 'go-live' })
          .catch(() => { failed.push(id); }),
      ),
    );
    setBulkBusy(false);
    if (failed.length > 0) {
      alert(`${targetIds.length - failed.length} succeeded, ${failed.length} failed. Reloading list.`);
    }
    setSelected(new Set());
    await load();
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Events</h1>
          <p className="text-sm text-gray-600 mt-0.5">
            All workshops, webinars, and instance events. Templates are managed
            from Series.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => (window.location.hash = '#admin/events/new')}
            className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-1.5 text-sm hover:bg-uif-green-dark"
          >
            <Plus className="h-4 w-4" /> Add event
          </button>
          <button
            type="button"
            onClick={() => (window.location.hash = '#admin/events/bulk')}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            <Upload className="h-4 w-4" /> Bulk upload
          </button>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">
          {error}
        </div>
      )}

      {/* Filter bar */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-3 flex-wrap">
        <div className="flex gap-1" role="tablist">
          <ScopeButton active={scope === 'upcoming'}  onClick={() => setScope('upcoming')}  label="Upcoming" />
          <ScopeButton active={scope === 'all'}       onClick={() => setScope('all')}       label="All events" />
          <ScopeButton active={scope === 'drafts'}    onClick={() => setScope('drafts')}    label={`Drafts${draftCountLabel(events)}`} />
          <ScopeButton active={scope === 'templates'} onClick={() => setScope('templates')} label="Templates" />
        </div>
        <div className="flex items-center gap-2 flex-1 min-w-[180px] relative">
          <Search className="h-4 w-4 text-gray-400 absolute left-2 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title, slug, or series…"
            className="flex-1 rounded-md border border-gray-300 pl-8 pr-3 py-1.5 text-sm"
          />
        </div>
      </div>

      {/* Bulk action bar — only renders when something is selected. */}
      {selected.size > 0 && (
        <div className="bg-uif-green/10 border border-uif-green/30 rounded-lg p-3 flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm text-uif-green-dark font-medium">
            {selected.size} selected
            {selectionSummary.drafts > 0 && selectionSummary.live > 0 && (
              <span className="text-xs text-gray-600 ml-2">
                ({selectionSummary.drafts} draft · {selectionSummary.live} live)
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {selectionSummary.drafts > 0 && (
              <button
                type="button"
                onClick={() => runBulk('go-live')}
                disabled={bulkBusy}
                className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-1.5 text-sm hover:bg-uif-green-dark disabled:opacity-60"
              >
                {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Go Live ({selectionSummary.drafts})
              </button>
            )}
            {selectionSummary.live > 0 && (
              <button
                type="button"
                onClick={() => runBulk('unpublish')}
                disabled={bulkBusy}
                className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-sm text-amber-800 hover:bg-amber-50 disabled:opacity-60"
              >
                {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                Move to draft ({selectionSummary.live})
              </button>
            )}
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              disabled={bulkBusy}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
              title="Clear selection"
            >
              <X className="h-3 w-3" /> Clear
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-3 py-2 w-8">
                <input
                  type="checkbox"
                  aria-label="Select all visible"
                  checked={sorted.length > 0 && sorted.every((e) => selected.has(e.calendar_entry_id))}
                  onChange={toggleSelectAll}
                  className="h-4 w-4 accent-uif-green"
                />
              </th>
              <SortableTh label="Title"    activeKey={sortKey} dir={sortDir} sortKey="title"    onClick={toggleSort} />
              <SortableTh label="Date"     activeKey={sortKey} dir={sortDir} sortKey="date"     onClick={toggleSort} />
              <SortableTh label="Series"   activeKey={sortKey} dir={sortDir} sortKey="series"   onClick={toggleSort} />
              <SortableTh label="Audience" activeKey={sortKey} dir={sortDir} sortKey="audience" onClick={toggleSort} />
              <SortableTh label="Status"   activeKey={sortKey} dir={sortDir} sortKey="status"   onClick={toggleSort} />
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">Loading…</td></tr>
            )}
            {!loading && sorted.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No events match this view.</td></tr>
            )}
            {sorted.map((e) => (
              <tr key={e.calendar_entry_id} className={'hover:bg-gray-50 ' + (selected.has(e.calendar_entry_id) ? 'bg-uif-green/5' : '')}>
                <td className="px-3 py-3 align-top">
                  <input
                    type="checkbox"
                    aria-label={`Select ${e.title}`}
                    checked={selected.has(e.calendar_entry_id)}
                    onChange={() => toggleSelect(e.calendar_entry_id)}
                    className="h-4 w-4 accent-uif-green"
                  />
                </td>
                <Td>
                  <div className="font-medium text-gray-900">{e.title}</div>
                  <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                    {e.is_recurring_template && (
                      <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-purple-100 text-purple-800">
                        Template
                      </span>
                    )}
                    {e.parent_template_id != null && (
                      <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">
                        Instance
                      </span>
                    )}
                    {e.slug && (
                      <span className="text-xs text-gray-500 truncate">/{e.slug}</span>
                    )}
                  </div>
                </Td>
                <Td>
                  <div className="text-xs text-gray-700">{formatEventDate(e)}</div>
                </Td>
                <Td>
                  {e.series_label ? (
                    <div className="inline-flex items-center gap-1 text-xs text-gray-700">
                      <RepeatIcon className="h-3 w-3" />
                      {e.series_label}
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </Td>
                <Td>
                  <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 uppercase tracking-wider">
                    {e.audience || 'public'}
                  </span>
                </Td>
                <Td>
                  <StatusBadge isDraft={!!e.is_draft} />
                </Td>
                <Td>
                  <div className="flex items-center gap-2">
                    <a
                      href={`#admin-event-edit/${e.calendar_entry_id}`}
                      className="inline-flex items-center gap-1 text-xs text-gray-700 hover:text-uif-green-dark"
                    >
                      <Pencil className="h-3 w-3" /> Edit
                    </a>
                    {!e.is_recurring_template && (
                      <a
                        href={`#admin/budgets/${e.calendar_entry_id}`}
                        className="inline-flex items-center gap-1 text-xs text-gray-700 hover:text-uif-green-dark"
                        title="Open per-event budget"
                      >
                        <DollarSign className="h-3 w-3" /> Budget
                      </a>
                    )}
                    {!e.is_recurring_template && (
                      <a
                        href={e.slug ? `#e/${e.slug}` : `#event/${e.event_id}`}
                        className="inline-flex items-center gap-1 text-xs text-gray-700 hover:text-uif-green-dark"
                      >
                        <ExternalLink className="h-3 w-3" /> View
                      </a>
                    )}
                    {e.slug && (
                      <button
                        type="button"
                        onClick={() => navigator.clipboard?.writeText(window.location.origin + '/#e/' + e.slug)}
                        className="inline-flex items-center gap-1 text-xs text-gray-700 hover:text-uif-green-dark"
                        title="Copy public link"
                      >
                        <Copy className="h-3 w-3" /> Copy URL
                      </button>
                    )}
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ScopeButton({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      role="tab"
      aria-selected={active}
      className={
        'rounded-md px-3 py-1.5 text-sm font-medium transition-colors ' +
        (active
          ? 'bg-uif-green text-white'
          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50')
      }
    >
      {label}
    </button>
  );
}

function draftCountLabel(events: PublicEvent[]): string {
  const n = events.filter((e) => e.is_draft && !e.is_recurring_template).length;
  return n > 0 ? ` (${n})` : '';
}

function StatusBadge({ isDraft }: { isDraft: boolean }) {
  if (isDraft) {
    return (
      <span className="inline-flex items-center text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-800">
        Draft
      </span>
    );
  }
  return (
    <span className="inline-flex items-center text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded bg-uif-green/10 text-uif-green-dark">
      Live
    </span>
  );
}

function formatEventDate(e: PublicEvent): string {
  const s = new Date(e.start_date);
  const en = new Date(e.end_date);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' };
  const sameDay = s.toDateString() === en.toDateString();
  if (sameDay) return s.toLocaleDateString('en-US', opts);
  return `${s.toLocaleDateString('en-US', opts)} – ${en.toLocaleDateString('en-US', opts)}`;
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{children}</th>;
}

function SortableTh({ label, activeKey, dir, sortKey, onClick }: {
  label: string; activeKey: SortKey; dir: SortDir; sortKey: SortKey; onClick: (k: SortKey) => void;
}) {
  const active = activeKey === sortKey;
  return (
    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
      <button
        type="button"
        onClick={() => onClick(sortKey)}
        className={
          'inline-flex items-center gap-1 select-none ' +
          (active ? 'text-uif-green-dark' : 'text-gray-600 hover:text-gray-900')
        }
        aria-label={`Sort by ${label}`}
      >
        {label}
        {active && (dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
      </button>
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return <td className="px-4 py-3 align-top">{children}</td>;
}
